"use client";

import React, { useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
// ✅ CRITICAL: Import directly from source, not from @/contexts barrel
import { useAuth } from "../contexts/AuthContext";
// ✅ CRITICAL: Import directly, not from @/ui/primitives barrel
// The barrel exports ALL primitives (DataTable, Tabs, Pagination, etc.) which aren't needed here
import { Skeleton } from "@/ui/primitives/Skeleton";
import type { UserRole } from "../contexts/AuthContext";

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRole?: UserRole;
  fallback?: React.ReactNode;
  redirectTo?: string;
}

/**
 * ProtectedRoute - Protects routes that require authentication
 *
 * Features:
 * - Redirects to login if not authenticated
 * - Shows loading state while checking auth
 * - Supports role-based access control
 * - Prevents infinite redirect loops
 * - Customizable fallback UI and redirect URLs
 *
 * Usage:
 * <ProtectedRoute requiredRole="admin" redirectTo="/unauthorized">
 *   <AdminSettings />
 * </ProtectedRoute>
 */
export function ProtectedRoute({
  children,
  requiredRole,
  fallback,
  redirectTo = "/unauthorized",
}: ProtectedRouteProps) {
  const { isAuthenticated, isPending: loading, hasRole } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const pathname = location.pathname;
  const redirectAttemptsRef = useRef(0);

  useEffect(() => {
    const currentPathname = pathname;

    // Prevent infinite redirect loops - max 3 attempts
    if (redirectAttemptsRef.current >= 3) {
      return;
    }

    // Don't redirect if still loading - wait for auth check to complete
    if (loading) {
      return;
    }

    const checkAuth = () => {
      // Redirect to login if not authenticated
      if (!isAuthenticated) {
        // Don't redirect if we're already on login page
        if (
          currentPathname === "/login" ||
          currentPathname?.startsWith("/login")
        ) {
          return;
        }

        redirectAttemptsRef.current += 1;
        // Store the intended destination for redirect after login
        const returnUrl = encodeURIComponent(currentPathname || "/home");
        navigate(`/login?returnUrl=${returnUrl}`);
        return;
      }

      // Reset counter if authenticated
      if (isAuthenticated) {
        redirectAttemptsRef.current = 0;
      }
    };

    // Check immediately for better UX
    checkAuth();

    // Check role-based access control (only if authenticated and role required)
    if (isAuthenticated && requiredRole) {
      if (!hasRole(requiredRole)) {
        redirectAttemptsRef.current += 1;
        navigate(redirectTo);
        return;
      }
    }
  }, [
    isAuthenticated,
    loading,
    navigate,
    requiredRole,
    hasRole,
    redirectTo,
    pathname,
  ]);

  // Show loading state while checking authentication
  if (loading) {
    return (
      fallback || (
        <div className="min-h-screen bg-primary flex items-center justify-center">
          <div className="text-center">
            <Skeleton className="w-12 h-12 rounded-full mx-auto mb-4" />
            <p className="text-secondary text-meta-md">Loading...</p>
          </div>
        </div>
      )
    );
  }

  // Don't render children if not authenticated (will redirect)
  if (!isAuthenticated) {
    return null;
  }

  // Don't render children if role check fails (will redirect)
  if (requiredRole && !hasRole(requiredRole)) {
    return null;
  }

  return <>{children}</>;
}
